import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Sparkles, ChevronDown, ChevronRight, RefreshCcw, Wand2, User, MapPin, Hash, Clock, CheckCircle2, XCircle, PauseCircle, TimerReset, Trash2 } from "lucide-react";

function StatChip({ label, value, tone }) {
  const cls = tone === "ok" ? "text-emerald-400 border-emerald-500/30" :
              tone === "err" ? "text-[#FF3B30] border-[#FF3B30]/40" :
              tone === "wait" ? "text-amber-400 border-amber-500/30" :
              "text-zinc-300 border-white/10";
  return (
    <div className={`px-2 py-1 border ${cls} font-mono text-[10px] flex items-center gap-1.5`}>
      <span className="uppercase tracking-widest text-[9px] opacity-70">{label}</span>
      <span className="text-[12px]">{value}</span>
    </div>
  );
}

function TaskRow({ task, onDelete }) {
  const now = new Date().toISOString();
  const locked = task.status === "Awaiting Execution" && task.unlocks_at && task.unlocks_at > now;
  const statusColor = {
    "Completed": "text-emerald-400",
    "Verification Failed": "text-[var(--accent)]",
    "Cancelled": "text-zinc-500",
    "In Progress": "text-amber-400",
    "Awaiting Execution": locked ? "text-blue-400" : "text-white",
  }[task.status] || "text-zinc-300";

  return (
    <div className="grid grid-cols-12 py-2.5 px-3 border-b border-white/5 items-center text-[12px]" data-testid={`drift-task-row-${task.id}`}>
      <div className="col-span-2 font-mono text-zinc-400 truncate">{task.subreddit}</div>
      <div className="col-span-1 font-mono text-[10px] uppercase tracking-widest text-zinc-500">{task.action_type}</div>
      <div className="col-span-3 text-zinc-200 truncate">{task.topic || <span className="text-zinc-600">—</span>}</div>
      <div className="col-span-2 text-zinc-500 truncate italic">{task.task_script?.slice(0, 60) || task.notes?.slice(0, 60) || <span className="opacity-40">—</span>}</div>
      <div className="col-span-2 font-mono text-[10px] text-zinc-500">
        {task.unlocks_at ? new Date(task.unlocks_at).toLocaleString() : "—"}
      </div>
      <div className={`col-span-1 font-mono text-[10px] uppercase tracking-widest ${statusColor} text-right`}>
        {locked ? "LOCKED" : task.status.replace("Awaiting Execution", "READY").replace("Verification Failed", "FAILED")}
      </div>
      <div className="col-span-1 flex justify-end">
        <button
          onClick={(e) => { e.stopPropagation(); if (window.confirm("Delete this drift task?")) onDelete(task.id); }}
          data-testid={`delete-drift-task-${task.id}`}
          title="Delete task"
          className="p-1 border border-white/10 hover:border-[var(--accent)] text-zinc-500 hover:text-[var(--accent)]"
        >
          <Trash2 className="w-3 h-3"/>
        </button>
      </div>
    </div>
  );
}

function BucketCard({ bucket, onRegen, onDeleteTask }) {
  const [open, setOpen] = useState(false);
  const a = bucket.agent || {};
  const pct = bucket.pct || 0;
  const barColor = pct >= 80 ? "bg-emerald-400" : pct >= 40 ? "bg-amber-400" : "bg-[#FF3B30]";

  return (
    <div className="border border-white/10 hover:border-white/20 bg-[var(--surface)]" data-testid={`drift-bucket-${bucket.id}`}>
      <button onClick={() => setOpen(!open)}
              className="w-full grid grid-cols-12 items-center px-4 py-3 text-left"
              data-testid={`drift-bucket-toggle-${bucket.id}`}>
        <div className="col-span-1">
          {open ? <ChevronDown className="w-4 h-4 text-zinc-500"/> : <ChevronRight className="w-4 h-4 text-zinc-500"/>}
        </div>
        <div className="col-span-3">
          <div className="text-[14px] text-zinc-100 font-medium truncate">{a.name || bucket.agent_id.slice(0,8)}</div>
          {a.reddit_username && (
            <div className="font-mono text-[10px] text-zinc-500 truncate">@{a.reddit_username.replace(/^u\//,"")}</div>
          )}
        </div>
        <div className="col-span-2 font-mono text-[11px] text-zinc-400">{bucket.week}</div>
        <div className="col-span-4 pr-4">
          <div className="flex items-center gap-2">
            <div className="flex-1 h-1.5 bg-black/60 relative overflow-hidden">
              <div className={`h-full ${barColor} transition-all`} style={{width: `${pct}%`}}/>
            </div>
            <span className="font-mono text-[11px] text-zinc-400 w-12 text-right">{pct}%</span>
          </div>
          <div className="flex gap-1.5 mt-2 flex-wrap">
            <StatChip label="TOT" value={bucket.total}/>
            {bucket.completed > 0 && <StatChip label="OK" value={bucket.completed} tone="ok"/>}
            {bucket.failed > 0 && <StatChip label="FAIL" value={bucket.failed} tone="err"/>}
            {bucket.awaiting > 0 && <StatChip label="READY" value={bucket.awaiting} tone="wait"/>}
            {bucket.locked > 0 && <StatChip label="LOCKED" value={bucket.locked}/>}
            {bucket.cancelled > 0 && <StatChip label="CANC" value={bucket.cancelled}/>}
          </div>
        </div>
        <div className="col-span-2 text-right">
          <button
            onClick={(e) => { e.stopPropagation(); onRegen(bucket.agent_id); }}
            data-testid={`drift-regen-${bucket.agent_id}`}
            className="border border-white/15 hover:border-[#FF3B30] hover:text-[#FF3B30] text-zinc-400 px-3 py-1.5 text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1.5"
          >
            <RefreshCcw className="w-3 h-3"/> ADD MORE
          </button>
        </div>
      </button>
      {open && (
        <div className="border-t border-white/5 bg-[#050505]" data-testid={`drift-bucket-body-${bucket.id}`}>
          <div className="grid grid-cols-12 px-3 py-2 text-[9px] font-mono uppercase tracking-widest text-zinc-600 border-b border-white/5">
            <div className="col-span-2">Sub</div>
            <div className="col-span-1">Action</div>
            <div className="col-span-3">Topic</div>
            <div className="col-span-2">Script / Notes</div>
            <div className="col-span-2">Unlocks</div>
            <div className="col-span-1 text-right">Status</div>
            <div className="col-span-1 text-right">Del</div>
          </div>
          {bucket.tasks.map(t => <TaskRow key={t.id} task={t} onDelete={onDeleteTask}/>)}
        </div>
      )}
    </div>
  );
}

export default function DriftCampaigns() {
  const qc = useQueryClient();
  const [weeks, setWeeks] = useState(6);
  const q = useQuery({
    queryKey: ["drift-campaigns", weeks],
    queryFn: () => api.get(`/drift-campaigns?weeks=${weeks}`).then(r => r.data),
    refetchInterval: 30000,
  });
  const del = useMutation({
    mutationFn: (tid) => api.delete(`/tasks/${tid}`),
    onSuccess: () => { toast.success("Drift task deleted"); qc.invalidateQueries({queryKey: ["drift-campaigns"]}); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Delete failed"),
  });
  const [redditBind, setRedditBind] = useState(false);
  const gen = useMutation({
    mutationFn: (agent_id) => api.post("/persona-drift/generate",
      { ...(agent_id ? { agent_id } : {}), reddit_bind: redditBind },
      { timeout: 180000 }),
    onSuccess: (r) => {
      const bound = r.data.reddit_bound || 0;
      const boundNote = r.data.reddit_bind_requested
        ? (bound > 0 ? ` · ${bound} bound to specific posts` : " · Reddit rate-limited, kept loose")
        : "";
      toast.success(`Scheduled ${r.data.created} drift task(s) across ${Object.keys(r.data.per_agent || {}).length} agent(s)${boundNote}`);
      qc.invalidateQueries({queryKey: ["drift-campaigns"]});
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Generation failed"),
  });

  const buckets = q.data || [];
  const totals = buckets.reduce((acc, b) => ({
    total: acc.total + b.total,
    completed: acc.completed + b.completed,
    failed: acc.failed + b.failed,
  }), {total: 0, completed: 0, failed: 0});
  const overallPct = totals.total ? Math.round((totals.completed / totals.total) * 100) : 0;

  return (
    <Layout title="Drift ops." subtitle="Weekly persona activity — organic account maintenance"
      right={
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
          <label className="flex items-center gap-2 text-[11px] font-mono text-zinc-300 cursor-pointer" data-testid="drift-reddit-bind-toggle">
            <input
              type="checkbox"
              checked={redditBind}
              onChange={(e) => setRedditBind(e.target.checked)}
              className="accent-[#FF3B30]"
            />
            🎯 Bind to real reddit posts (tight)
          </label>
          <button
            onClick={() => gen.mutate(null)}
            disabled={gen.isPending}
            data-testid="generate-drift-all-btn"
            className="bg-[#FF3B30] hover:bg-[#FF5A50] text-white font-medium px-3 py-2 flex items-center gap-2 text-[13px] disabled:opacity-50"
          >
            {gen.isPending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Wand2 className="w-4 h-4"/>}
            Generate weekly drift · ALL AGENTS
          </button>
        </div>
      }
    >
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="border border-white/10 p-4 bg-[var(--surface)]">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">buckets</div>
          <div className="font-display font-black text-3xl tracking-tight">{buckets.length}</div>
        </div>
        <div className="border border-white/10 p-4 bg-[var(--surface)]">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">tasks scheduled</div>
          <div className="font-display font-black text-3xl tracking-tight">{totals.total}</div>
        </div>
        <div className="border border-emerald-500/20 p-4 bg-emerald-500/[0.03]">
          <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-500/70 mb-1">completed</div>
          <div className="font-display font-black text-3xl tracking-tight text-emerald-400">{totals.completed}</div>
        </div>
        <div className="border border-white/10 p-4 bg-[var(--surface)]">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">completion rate</div>
          <div className="font-display font-black text-3xl tracking-tight">{overallPct}%</div>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">// window</span>
        {[2,4,6,12].map(w => (
          <button key={w}
            onClick={() => setWeeks(w)}
            data-testid={`drift-window-${w}`}
            className={`px-3 py-1.5 text-[11px] font-mono border ${weeks===w ? "border-[#FF3B30] text-[#FF3B30]" : "border-white/10 text-zinc-400 hover:border-white/30"}`}>
            LAST {w}W
          </button>
        ))}
      </div>

      {q.isLoading && (
        <div className="text-zinc-500 text-sm p-6 border border-white/5">Loading drift buckets…</div>
      )}

      {!q.isLoading && buckets.length === 0 && (
        <div className="border border-dashed border-white/15 p-12 text-center bg-dots" data-testid="drift-empty">
          <User className="w-8 h-8 text-zinc-700 mx-auto mb-4"/>
          <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mb-2">// no drift activity</div>
          <div className="text-zinc-400 text-sm">No organic persona-drift tasks scheduled in the last {weeks} weeks. Click &ldquo;Generate weekly drift&rdquo; above to seed one week for every active agent.</div>
        </div>
      )}

      <div className="space-y-2">
        {buckets.map(b => <BucketCard key={b.id} bucket={b} onRegen={(id) => gen.mutate(id)} onDeleteTask={(tid) => del.mutate(tid)}/>)}
      </div>
    </Layout>
  );
}
